import { sanitizeExportColors } from './exportColors';
import { lexicalToHtml, replacePlaceholders } from './lexicalToHtml';
import { numberToBengaliWords } from './currencyWords';

const PAGE_SIZES = {
    A4:     { width: 794,  height: 1123, jsPdf: 'a4'     },
    A3:     { width: 1123, height: 1587, jsPdf: 'a3'     },
    A5:     { width: 559,  height: 794,  jsPdf: 'a5'     },
    LETTER: { width: 816,  height: 1056, jsPdf: 'letter' },
    LEGAL:  { width: 816,  height: 1344, jsPdf: 'legal'  },
};

const PAGE_MARGINS = {
    narrow:   { top: 48,  bottom: 48,  left: 36, right: 36 },
    normal:   { top: 72,  bottom: 72,  left: 72, right: 72 },
    moderate: { top: 60,  bottom: 60,  left: 54, right: 54 },
    wide:     { top: 96,  bottom: 96,  left: 96, right: 96 },
    stamp:    { top: 216, bottom: 72,  left: 18, right: 18 },
};

const formatNumberWithCommas = (raw) => {
    if (!raw) return '';
    const str = String(raw);
    const hasBangla = /[০-৯]/.test(str);
    const en = str.replace(/[০-৯]/g, (d) => '০১২৩৪৫৬৭৮৯'.indexOf(d).toString());
    const stripped = en.replace(/,/g, '');
    const [intPart, decPart] = stripped.split('.');
    const digits = (intPart || '').replace(/\D/g, '');
    if (!digits) return raw;
    const formatted = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const result = decPart !== undefined ? `${formatted}.${decPart}` : formatted;
    const BN = ['০','১','২','৩','৪','৫','৬','৭','৮','৯'];
    return hasBangla ? result.replace(/[0-9]/g, (d) => BN[d]) : result;
};

const toEnglishDigits = (s) => String(s ?? '').replace(/[০-৯]/g, (d) => '০১২৩৪৫৬৭৮৯'.indexOf(d));

const parseBnDateString = (value) => {
    if (!value) return null;
    const en = toEnglishDigits(value);
    const m = en.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return isNaN(d.getTime()) ? null : d;
};

const BN_DAYS = ['রবিবার', 'সোমবার', 'মঙ্গলবার', 'বুধবার', 'বৃহস্পতিবার', 'শুক্রবার', 'শনিবার'];

export function applyDocTransforms(rawValues, placeholders, wordsEnabled = {}) {
    if (!placeholders?.length) return rawValues;
    const result = { ...rawValues };
    placeholders.forEach((p) => {
        if (p.type === 'Currency' && result[p.slug]) {
            const formatted = formatNumberWithCommas(result[p.slug]);
            if (wordsEnabled[p.slug] !== false) {
                const words = numberToBengaliWords(result[p.slug]);
                result[p.slug] = words ? `${formatted} (${words})` : `${formatted} টাকা`;
            } else {
                result[p.slug] = `${formatted} টাকা`;
            }
        }
        if (p.type === 'DateBar' && result[p.slug]) {
            const date = parseBnDateString(result[p.slug]);
            if (date) {
                result[p.slug] = `${result[p.slug]} ইং তারিখ ${BN_DAYS[date.getDay()]}`;
            }
        }
    });
    return result;
}

export function resolvePageSettings(doc) {
    const md = doc?.metadata || {};
    const pageSize = PAGE_SIZES[String(md.pageSize || 'A4').toUpperCase()] || PAGE_SIZES.A4;
    const legacy = PAGE_MARGINS[String(md.margin || 'normal').toLowerCase()] || PAGE_MARGINS.normal;
    const margin = {
        top:    typeof md.marginTop    === 'number' ? md.marginTop    : legacy.top,
        bottom: typeof md.marginBottom === 'number' ? md.marginBottom : legacy.bottom,
        left:   typeof md.marginLeft   === 'number' ? md.marginLeft   : legacy.left,
        right:  typeof md.marginRight  === 'number' ? md.marginRight  : legacy.right,
    };
    return { pageSize, margin };
}

const toBnDigits = n => String(n).replace(/[0-9]/g, d => '০১২৩৪৫৬৭৮৯'[d]);
const PAGE_NUM_STYLE = `position:absolute;left:0;right:0;text-align:center;font-family:'Kalpurush','Noto Sans Bengali','SolaimanLipi',Arial,sans-serif;font-size:14px;color:#222;line-height:1.4;`;

function buildExportHtml(rawPages, pagePadding, pageHeight, margin, isStamp) {
    if (rawPages.length === 1) {
        return `<div style="${pagePadding}">${rawPages[0]}</div>`;
    }
    return rawPages.map((p, i) => {
        const isFirst = i === 0;
        const isLast  = i === rawPages.length - 1;
        const wrapStyle = `${pagePadding}position:relative;height:${pageHeight}px;overflow:hidden;display:block;`;
        const showNums  = isStamp && rawPages.length > 1;
        const topNum    = showNums && !isFirst ? `<div style="${PAGE_NUM_STYLE}top:${Math.max(8, margin.top - 28)}px;">পাতা নং-${toBnDigits(i + 1)}</div>` : '';
        const bottomNum = showNums && !isLast  ? `<div style="${PAGE_NUM_STYLE}bottom:${Math.max(8, margin.bottom - 28)}px;">চলমান-${toBnDigits(i + 2)}</div>` : '';
        return `<div style="${wrapStyle}">${topNum}${p}${bottomNum}</div>`;
    }).join('');
}

const EXPORT_CSS = `
    [data-mantine-color-scheme="light"]{background:#fff!important;color:#1a1a1a!important;color-scheme:light!important}
    [data-mantine-color-scheme="light"] p,
    [data-mantine-color-scheme="light"] span,
    [data-mantine-color-scheme="light"] li,
    [data-mantine-color-scheme="light"] td,
    [data-mantine-color-scheme="light"] th,
    [data-mantine-color-scheme="light"] h1,
    [data-mantine-color-scheme="light"] h2,
    [data-mantine-color-scheme="light"] h3,
    [data-mantine-color-scheme="light"] h4,
    [data-mantine-color-scheme="light"] h5,
    [data-mantine-color-scheme="light"] h6{color:#1a1a1a!important}
    p{margin:0;font-size:13px;line-height:1.9;min-height:1.9em}
    strong,b{font-weight:700} em,i{font-style:italic} u{text-decoration:underline}
    s{text-decoration:line-through} sub{vertical-align:sub;font-size:.8em} sup{vertical-align:super;font-size:.8em}
    h1{font-size:2em;font-weight:700;margin:.5em 0 .3em;line-height:1.3}
    h2{font-size:1.6em;font-weight:700;margin:.5em 0 .3em;line-height:1.3}
    h3{font-size:1.3em;font-weight:600;margin:.5em 0 .3em;line-height:1.4}
    h4{font-size:1.1em;font-weight:600;margin:.4em 0 .2em}
    h5{font-size:1em;font-weight:600;margin:.4em 0 .2em}
    h6{font-size:.95em;font-weight:600;margin:.4em 0 .2em}
    ul{list-style-type:disc;padding-left:1.5em;margin:.4em 0}
    ol{list-style-type:decimal;padding-left:1.5em;margin:.4em 0}
    ul ul{list-style-type:circle} ul ul ul{list-style-type:square}
    li{font-size:13px;line-height:1.9;display:list-item}
    blockquote{border-left:3px solid #ccc;margin:.6em 0;padding:.3em .8em;color:#555;font-style:italic}
    table{width:100%;border-collapse:collapse!important;border-spacing:0!important;background:transparent!important;margin:.6em 0;font-size:.92em}
    td,th{border:1px solid #aaa!important;padding:5px 8px;vertical-align:top}
    th{font-weight:600}
    pre,code{font-family:'Courier New',monospace;background:#f4f4f4;border-radius:3px;font-size:.88em}
    pre{padding:.6em .8em;margin:.4em 0;white-space:pre-wrap;word-break:break-all}
    code{padding:1px 4px}
    hr{border:none;border-top:1px solid #ccc;margin:.8em 0}
    a{color:#1971c2;text-decoration:underline}
    .doc-value-filled,.doc-value-unfilled{background:transparent!important;color:inherit!important;padding:0!important;border-radius:0!important;}
    /* Mirror the preview (DocPreviewPanel.module.scss): strip Lexical's inline
       font-size/family from plain text spans so they inherit the block's 13px.
       Without this the global \`span{font-size:12px}\` in global.scss leaks into the
       captured element (it's appended to document.body), shrinking body text to 12px
       in the PDF while the preview shows 13px. Exclude placeholder value/token spans
       so any font-size an admin set on a placeholder is preserved — same as preview. */
    p span:not(.doc-value-filled):not(.doc-value-unfilled):not(.doc-placeholder-token),
    li span:not(.doc-value-filled):not(.doc-value-unfilled):not(.doc-placeholder-token){font-size:inherit!important;font-family:inherit!important}
`;

/**
 * Generate a PDF blob for a single template doc.
 *
 * @param {object} templateDoc       - doc record with contentKey and metadata (pageSize, margin, stamp…)
 * @param {Array}  placeholders      - placeholder definitions from the template set
 * @param {object} fieldValues       - { [slug]: value } — user-filled form data
 * @param {object} [options]
 * @param {object} [options.currencyWordsEnabled] - per-slug bool for Bengali word rendering (default all true)
 * @param {string} [options.r2Url]               - R2 public base URL (falls back to VITE_R2_PUBLIC_URL)
 * @param {string} [options.rawHtml]             - pre-rendered HTML; skips R2 fetch + Lexical conversion
 * @returns {Promise<Blob>}
 */
export async function generateDocPdf(templateDoc, placeholders, fieldValues, options = {}) {
    const {
        currencyWordsEnabled = {},
        r2Url = import.meta.env.VITE_R2_PUBLIC_URL || '',
        rawHtml = null,
    } = options;

    const { pageSize, margin } = resolvePageSettings(templateDoc);

    let finalHtml = '';
    if (rawHtml !== null) {
        finalHtml = rawHtml;
    } else if (templateDoc?.contentKey) {
        const res = await fetch(`${r2Url}/${templateDoc.contentKey}?t=${Date.now()}`);
        if (res.ok) {
            const json = await res.json();
            const raw = lexicalToHtml(json);
            const transformed = applyDocTransforms(fieldValues, placeholders, currencyWordsEnabled);
            finalHtml = replacePlaceholders(raw, transformed);
        }
    }

    const rawPages = finalHtml
        ? finalHtml.split(/<div[^>]*class="doc-page-break"[^>]*>[^<]*<\/div>/gi)
        : [''];
    while (rawPages.length > 1 && !rawPages[rawPages.length - 1].trim()) rawPages.pop();

    const isStamp = templateDoc?.metadata?.stamp === true;
    const pagePadding = `padding:${margin.top}px ${margin.right}px ${margin.bottom}px ${margin.left}px;box-sizing:border-box;`;
    const exportHtml  = buildExportHtml(rawPages, pagePadding, pageSize.height, margin, isStamp);

    const el = document.createElement('div');
    el.style.cssText = [
        `width:${pageSize.width}px`,
        'box-sizing:border-box', 'background:#fff', 'color:#1a1a1a',
        "font-family:'Kalpurush','Noto Sans Bengali','SolaimanLipi',Arial,sans-serif",
        'font-size:13px', 'line-height:1.9', 'word-break:break-word',
        'color-scheme:light',
    ].join(';');
    // Force Mantine light theme so dark-mode CSS vars don't bleed into the captured element
    el.setAttribute('data-mantine-color-scheme', 'light');
    el.innerHTML = exportHtml;

    const styleTag = document.createElement('style');
    styleTag.textContent = EXPORT_CSS;
    document.head.appendChild(styleTag);
    document.body.appendChild(el);

    // Block until self-hosted Kalpurush/SolaimanLipi are fully loaded — otherwise
    // html2canvas can snapshot text before the @font-face fetch resolves and bake
    // Arial fallbacks into the PDF even though the preview later renders correctly.
    if (document.fonts?.ready) {
        try { await document.fonts.ready; } catch { /* ignore */ }
    }

    try {
        // Lazy-load html2canvas + jsPDF — same settings html2pdf.js used internally
        const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
            import('html2canvas'),
            import('jspdf'),
        ]);

        // Capture full element at 2x scale (identical to html2pdf's html2canvas config)
        const canvas = await html2canvas(el, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            // html2canvas can't parse oklch/lab/color() — rewrite any to rgb first.
            onclone: (d) => sanitizeExportColors(d.documentElement),
        });

        const pdf = new jsPDF({ unit: 'mm', format: pageSize.jsPdf, orientation: 'portrait' });
        const pdfW = pdf.internal.pageSize.getWidth();
        const pdfH = pdf.internal.pageSize.getHeight();

        const canvasW = canvas.width;
        const canvasH = canvas.height;

        // How many canvas pixels equal one PDF page height (with margin: 0, same as html2pdf)
        const pageHeightPx = Math.round((pdfH / pdfW) * canvasW);
        const totalPages   = Math.ceil(canvasH / pageHeightPx);

        for (let i = 0; i < totalPages; i++) {
            if (i > 0) pdf.addPage();

            const srcY  = i * pageHeightPx;
            const srcH  = Math.min(pageHeightPx, canvasH - srcY);

            // Slice canvas to exactly this page's content
            const slice = document.createElement('canvas');
            slice.width  = canvasW;
            slice.height = srcH;
            slice.getContext('2d').drawImage(canvas, 0, srcY, canvasW, srcH, 0, 0, canvasW, srcH);

            const imgData      = slice.toDataURL('image/jpeg', 0.98);
            const sliceHeightMm = (srcH / canvasW) * pdfW;
            pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, sliceHeightMm);
        }

        return pdf.output('blob');
    } finally {
        document.body.removeChild(el);
        document.head.removeChild(styleTag);
    }
}

/** Trigger a browser download of a Blob. */
export function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * Print a PDF Blob from the current tab. Pages are rendered to images and
 * printed from a hidden same-origin iframe: printing a blob PDF through the
 * browser's PDF viewer is blocked, which is what used to force a new tab.
 *
 * Falls back to a pop-up tab if rendering fails. Resolves false only when that
 * pop-up was blocked.
 */
export async function printBlob(blob) {
    let frame;
    try {
        const pdfjsLib = await import('pdfjs-dist');
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
            'pdfjs-dist/build/pdf.worker.min.mjs',
            import.meta.url,
        ).toString();
        const doc = await pdfjsLib.getDocument({ data: new Uint8Array(await blob.arrayBuffer()) }).promise;

        const pages = [];
        for (let i = 1; i <= doc.numPages; i++) {
            const page = await doc.getPage(i);
            const base = page.getViewport({ scale: 1 });
            const viewport = page.getViewport({ scale: 2 });
            const canvas = document.createElement('canvas');
            canvas.width = Math.ceil(viewport.width);
            canvas.height = Math.ceil(viewport.height);
            await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
            pages.push({ src: canvas.toDataURL('image/jpeg', 0.92), w: base.width, h: base.height });
            page.cleanup();
        }
        doc.destroy();

        frame = document.createElement('iframe');
        frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
        document.body.appendChild(frame);
        const d = frame.contentDocument;
        d.open();
        const sizeKey = (pg) => `${Math.round(pg.w)}x${Math.round(pg.h)}`;
        const sizes = [...new Map(pages.map((pg) => [sizeKey(pg), pg])).values()];
        // Merged PDFs mix page sizes, so each size gets its own named @page.
        d.write(`<!doctype html><html><head><style>
            @page { margin: 0; }
            ${sizes.map((pg) => `@page p${sizeKey(pg)} { size: ${pg.w}pt ${pg.h}pt; margin: 0; }`).join('\n')}
            html, body { margin: 0; padding: 0; background: #fff; }
            img { display: block; break-after: page; page-break-after: always; }
            img:last-child { break-after: auto; page-break-after: auto; }
        </style></head><body>${pages
            .map((pg) => `<img src="${pg.src}" style="page:p${sizeKey(pg)};width:${pg.w}pt;height:${pg.h}pt">`)
            .join('')}</body></html>`);
        d.close();
        await Promise.all(
            [...d.images].map((img) => (img.complete ? null : new Promise((r) => { img.onload = img.onerror = r; }))),
        );
        const f = frame;
        f.contentWindow.focus();
        f.contentWindow.print();
        setTimeout(() => f.remove(), 60000);
        return true;
    } catch {
        frame?.remove();
        const win = window.open(URL.createObjectURL(blob), '_blank');
        if (!win) return false;
        win.addEventListener('load', () => setTimeout(() => win.print(), 500));
        return true;
    }
}

/**
 * Share blobs via Web Share API, falling back to individual downloads.
 * Returns true if Web Share was used, false if fallback download was triggered.
 */
export async function shareBlobs(files, title = '') {
    if (navigator.canShare && navigator.canShare({ files })) {
        await navigator.share({ title, files });
        return true;
    }
    files.forEach(f => downloadBlob(f, f.name));
    return false;
}
